import { ref, getCurrentInstance } from '@vue/composition-api'
import competitorsApi from '@/services/api/competitors'

// Filters whose option lists are small (single/double digits per committee) — loaded in full,
// no pagination, but drafts/events are re-fetched whenever year/draft_id changes (cascade).
const SIMPLE_FILTER_KEYS = ['nomination_status', 'years', 'drafts', 'events']
// Filters with hundreds-thousands of options per committee — cursor-paginated + server search.
const CURSOR_FILTER_KEYS = ['members', 'horses']

export default function useCompetitorsList() {
  const { proxy } = getCurrentInstance()
  const route = proxy.$route
  const router = proxy.$router

  const competitors = ref([])
  const meta = ref(null)
  const filters = ref({
    competitor_id: route.query.competitor_id ? Number(route.query.competitor_id) : null,
    nomination_status: route.query.nomination_status || null,
    horse_id: route.query.horse_id ? Number(route.query.horse_id) : null,
    year: route.query.year ? Number(route.query.year) : null,
    draft_id: route.query.draft_id ? Number(route.query.draft_id) : null,
    event_id: route.query.event_id ? Number(route.query.event_id) : null,
    page: route.query.page ? Number(route.query.page) : 1
  })

  const filterOptions = ref({
    members: [],
    horses: [],
    nomination_status: [],
    years: [],
    drafts: [],
    events: []
  })
  const filterLoading = ref({
    members: false,
    horses: false,
    nomination_status: false,
    years: false,
    drafts: false,
    events: false
  })
  const filterLoadingMore = ref({
    members: false,
    horses: false
  })
  const filtersLoaded = ref({
    nomination_status: false,
    years: false
  })
  // Cursor state kept per cursor-paginated filter: current search term + next page cursor.
  const cursorState = ref({
    members: { search: '', nextCursor: null },
    horses: { search: '', nextCursor: null }
  })

  const loading = ref(false)

  function syncQueryParams() {
    const query = {}
    if (filters.value.competitor_id) query.competitor_id = String(filters.value.competitor_id)
    if (filters.value.nomination_status) query.nomination_status = filters.value.nomination_status
    if (filters.value.horse_id) query.horse_id = String(filters.value.horse_id)
    if (filters.value.year) query.year = String(filters.value.year)
    if (filters.value.draft_id) query.draft_id = String(filters.value.draft_id)
    if (filters.value.event_id) query.event_id = String(filters.value.event_id)
    if (filters.value.page > 1) query.page = String(filters.value.page)
    router.replace({ query }).catch(() => {})
  }

  function buildFilterParams() {
    const params = {}
    if (filters.value.competitor_id) params.competitor_id = filters.value.competitor_id
    if (filters.value.nomination_status) params.nomination_status = filters.value.nomination_status
    if (filters.value.horse_id) params.horse_id = filters.value.horse_id
    if (filters.value.year) params.year = filters.value.year
    if (filters.value.draft_id) params.draft_id = filters.value.draft_id
    if (filters.value.event_id) params.event_id = filters.value.event_id
    return params
  }

  async function fetchList() {
    loading.value = true
    try {
      const params = buildFilterParams()
      if (filters.value.page > 1) params.page = filters.value.page

      const res = await competitorsApi.getList(params)
      competitors.value = res.data.data
      meta.value = res.data.meta != null ? res.data.meta : null
    } catch (_) {
      competitors.value = []
    } finally {
      loading.value = false
    }
  }

  // Small unpaginated lists: nomination_status/years load once, drafts/events reload on cascade.
  async function loadSimpleFilter(filterKey) {
    if (filterKey === 'nomination_status' || filterKey === 'years') {
      if (filtersLoaded.value[filterKey]) return
    }
    filterLoading.value[filterKey] = true
    try {
      const params = { type: filterKey }
      if (filterKey === 'drafts' && filters.value.year) params.year = filters.value.year
      if (filterKey === 'events') {
        if (filters.value.draft_id) params.draft_id = filters.value.draft_id
        else if (filters.value.year) params.year = filters.value.year
      }
      const res = await competitorsApi.getFilters(params)
      filterOptions.value[filterKey] = res.data.data[filterKey]
      if (filterKey === 'nomination_status' || filterKey === 'years') filtersLoaded.value[filterKey] = true
    } catch (_) {
      // keep previous options
    } finally {
      filterLoading.value[filterKey] = false
    }
  }

  // Cursor-paginated members/horses: reset=true starts a fresh search from page 1.
  async function loadCursorFilter(filterKey, { reset = false } = {}) {
    if (reset) {
      cursorState.value[filterKey] = { search: cursorState.value[filterKey].search, nextCursor: null }
      filterOptions.value[filterKey] = []
    } else if (filterOptions.value[filterKey].length) {
      return // already loaded once for the current open/search
    }

    filterLoading.value[filterKey] = true
    try {
      const params = { type: filterKey, per_page: 10 }
      if (cursorState.value[filterKey].search) params.search = cursorState.value[filterKey].search
      const res = await competitorsApi.getFilters(params)
      filterOptions.value[filterKey] = res.data.data[filterKey]
      cursorState.value[filterKey].nextCursor = res.data.data.next_cursor
    } catch (_) {
      filterOptions.value[filterKey] = []
    } finally {
      filterLoading.value[filterKey] = false
    }
  }

  async function loadMoreOptions(filterKey) {
    if (!CURSOR_FILTER_KEYS.includes(filterKey)) return
    const state = cursorState.value[filterKey]
    if (!state.nextCursor || filterLoadingMore.value[filterKey] || filterLoading.value[filterKey]) return

    filterLoadingMore.value[filterKey] = true
    try {
      const params = { type: filterKey, per_page: 10, cursor: state.nextCursor }
      if (state.search) params.search = state.search
      const res = await competitorsApi.getFilters(params)
      filterOptions.value[filterKey] = [...filterOptions.value[filterKey], ...res.data.data[filterKey]]
      state.nextCursor = res.data.data.next_cursor
    } catch (_) {
      // keep what's already loaded
    } finally {
      filterLoadingMore.value[filterKey] = false
    }
  }

  async function searchOptions(filterKey, query) {
    if (!CURSOR_FILTER_KEYS.includes(filterKey)) return
    cursorState.value[filterKey].search = query
    await loadCursorFilter(filterKey, { reset: true })
  }

  async function loadFilterOptions(filterKey) {
    if (CURSOR_FILTER_KEYS.includes(filterKey)) {
      await loadCursorFilter(filterKey, { reset: !filterOptions.value[filterKey].length })
      return
    }
    if (SIMPLE_FILTER_KEYS.includes(filterKey)) {
      await loadSimpleFilter(filterKey)
    }
  }

  function setFilter(key, val) {
    filters.value[key] = val
    filters.value.page = 1

    if (key === 'year') {
      filters.value.draft_id = null
      filters.value.event_id = null
      filtersLoaded.value.drafts = false
      loadSimpleFilter('drafts')
      loadSimpleFilter('events')
    }
    if (key === 'draft_id') {
      filters.value.event_id = null
      loadSimpleFilter('events')
    }

    syncQueryParams()
    fetchList()
  }

  function resetFilters() {
    filters.value = {
      competitor_id: null,
      nomination_status: null,
      horse_id: null,
      year: null,
      draft_id: null,
      event_id: null,
      page: 1
    }
    loadSimpleFilter('drafts')
    loadSimpleFilter('events')
    syncQueryParams()
    fetchList()
  }

  function setPage(page) {
    filters.value.page = page
    syncQueryParams()
    fetchList()
  }

  const downloadingFormat = ref(null)

  async function downloadFile(format) {
    if (downloadingFormat.value != null) return
    downloadingFormat.value = format
    try {
      const params = buildFilterParams()
      params.format = format

      const response = await competitorsApi.exportFile(params)

      if (format === 'pdf') {
        const blobUrl = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
        window.open(blobUrl, '_blank')
        return
      }

      const url = URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'competitors.xlsx')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } finally {
      downloadingFormat.value = null
    }
  }

  return {
    competitors,
    meta,
    filters,
    filterOptions,
    filterLoading,
    filterLoadingMore,
    loading,
    downloadingFormat,
    fetchList,
    loadFilterOptions,
    loadMoreOptions,
    searchOptions,
    setFilter,
    resetFilters,
    setPage,
    downloadFile
  }
}



// WEBPACK FOOTER //
// ./src/composables/competitors/useCompetitorsList.js